import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.onHistoryChange = this.onHistoryChange.bind(this)
    this.onClickFeaturedCollection = this.onClickFeaturedCollection.bind(this)
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500).bind(this)
  }

  connectedCallback() {
    this.submitInputs = this.querySelectorAll('.js-filter-submit')
    this.sortInputs = this.querySelectorAll('.js-sort-input')
    this.closeButton = this.querySelector('.js-close')
    this.featuredCollectionItems = this.querySelectorAll(
      '.js-featured-collection-item'
    )

    this.bindActiveFacetButtonEvents()
    window.addEventListener('popstate', this.onHistoryChange)
    this.submitInputs?.forEach((element) => {
      element.addEventListener('click', this.debouncedOnSubmit)
    })
    this.sortInputs?.forEach((element) => {
      element.addEventListener('click', this.debouncedOnSubmit)
    })
    this.featuredCollectionItems?.forEach((element) => {
      element.addEventListener('click', this.onClickFeaturedCollection)
    })
  }

  disconnectedCallback() {
    this.submitInputs = this.querySelectorAll('.js-filter-submit')
    this.sortInputs = this.querySelectorAll('.js-sort-input')
    this.closeButton = this.querySelector('.js-close')
    this.featuredCollectionItems = this.querySelectorAll(
      '.js-featured-collection-item'
    )

    window.removeEventListener('popstate', this.onHistoryChange)
    this.submitInputs?.forEach((element) => {
      element.removeEventListener('click', this.debouncedOnSubmit)
    })
    this.sortInputs?.forEach((element) => {
      element.removeEventListener('click', this.debouncedOnSubmit)
    })
    this.featuredCollectionItems?.forEach((element) => {
      element.removeEventListener('click', this.onClickFeaturedCollection)
    })
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest('form'))

    let searchParams = new URLSearchParams(formData)
    const hasFilters = Array.from(searchParams.keys()).some(
      (key) =>
        key.startsWith('filter.') && !key.startsWith('filter.v.availability')
    )

    const parent = event.target.closest('hh-checkbox-wrapper')
    const isAvailabilityFilter = parent?.dataset.label === 'Availability'
    if (!isAvailabilityFilter) {
      hasFilters
        ? searchParams.set('filter.v.availability', '1')
        : searchParams.delete('filter.v.availability')
    }

    this.renderPage(searchParams.toString(), event)
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  onClickFeaturedCollection(event) {
    event.preventDefault()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  renderPage(searchParams, event, updateURLHash = true) {
    document
      .getElementById('CollectionProductGrid')
      .querySelector('.js-product-grid')
      .classList.add('loading')

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${searchParams}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl)
      ? this.renderSectionFromCache(filterDataUrl, event)
      : this.renderSectionFromFetch(url, event)

    if (updateURLHash) this.updateURLHash(searchParams)
  }

  renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.json())
      .then((parsed) => {
        const html = Object.values(parsed).join('')
        this.filterData = [...this.filterData, { html, url }]
        this.renderFilters(html, event)
        this.renderProductGrid(html)
      })
  }

  renderSectionFromCache(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html)
  }

  renderProductGrid(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('CollectionProductGrid').innerHTML

    document.getElementById('CollectionProductGrid').innerHTML = innerHTML
    document
      .querySelectorAll('[data-featured-link]')
      .forEach((nonAnchorLink) => {
        nonAnchorLink.addEventListener('click', () => {
          const link = nonAnchorLink.getAttribute('data-featured-link')
          window.location.href = link
        })
      })
  }

  renderFilters(...args) {
    this.renderSingleFormFilters('#CollectionFiltersForm', ...args)
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const facetsToRender = parsedHTML.querySelectorAll(
      '#CollectionFiltersForm .js-filter'
    )

    facetsToRender.forEach((element) => {
      document.querySelector(
        `#CollectionFiltersForm .js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML
    })
    const featuredCollectionToRender = parsedHTML.querySelector(
      '.js-featured-collections'
    )

    if (featuredCollectionToRender) {
      document.querySelector('.js-featured-collections').innerHTML =
        featuredCollectionToRender.innerHTML
      this.featuredCollectionItems = this.querySelectorAll(
        '.js-featured-collection-item'
      )
      this.featuredCollectionItems?.forEach((element) => {
        element.addEventListener('click', this.onClickFeaturedCollection)
      })
    }

    this.renderActiveFacets(parsedHTML)

    const submitInputs = this.querySelectorAll('.js-filter-submit')
    submitInputs?.forEach((element) => {
      element.addEventListener('click', this.debouncedOnSubmit)
    })

    this.rebindSortInput(parsedHTML, event)
  }

  rebindSortInput(html) {
    const sortToRender = html.querySelector(
      '#CollectionFiltersForm .js-sort-container'
    )
    document.querySelector(
      '#CollectionFiltersForm .js-sort-container'
    ).innerHTML = sortToRender.innerHTML
    this.sortInputs = this.querySelectorAll('.js-sort-input')

    this.sortInputs?.forEach((element) => {
      element.addEventListener('click', this.debouncedOnSubmit)
    })
  }

  renderActiveFacets(html) {
    const activeFacetsElement = html.querySelector('.js-facets')
    if (!activeFacetsElement) return
    document.querySelector('.js-facets').innerHTML =
      activeFacetsElement.innerHTML

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
    this.resetCount()
    this.toggleClearAllButton()
  }

  resetCount() {
    const facetCount =
      document.querySelectorAll(
        '.collection-filters__facet.js-facet-remove:not(.collection-filters__facet-fc)'
      )?.length || 0
    const facetCountEl = document.querySelector('.js-facet-count')
    if (facetCountEl) {
      facetCountEl.textContent = `(${facetCount})`
      if (facetCount > 0) {
        facetCountEl.classList.remove('hidden')
      } else {
        facetCountEl.classList.add('hidden')
      }
    }
  }

  toggleClearAllButton() {
    const facetCount =
      document.querySelectorAll('.collection-filters__facet.js-facet-remove')
        ?.length || 0
    const removeButtons = document.querySelectorAll('.js-clear-all')
    removeButtons.forEach((element) => {
      if (facetCount > 0) {
        element.classList.remove('hidden')
      } else {
        element.classList.add('hidden')
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters')?.dataset
          ?.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
      {
        id: 'main-search-filters',
        section: document.getElementById('main-search-filters')?.dataset?.id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
